import {
  ContainerOutlined,
  EditOutlined,
  EllipsisOutlined,
  FileOutlined,
  FileTextOutlined,
  FolderOutlined,
  FormOutlined,
  FunctionOutlined,
  GroupOutlined,
  MailOutlined,
} from '@ant-design/icons';
import { type ReactNode } from 'react';
import { PlaceholderCategoryGroupMenuLabel } from './PlaceholderCategoryGroupMenuLabel.tsx';
import { type PlaceholderCategory, PlaceholderCategoryEnum } from './types.ts';

export enum PlaceholderCategoryGroupEnum {
  Backoffice = 'BACKOFFICE',
  Frontoffice = 'FRONTOFFICE',
}
export type PlaceholderCategoryGroup =
  (typeof placeholderCategoryGroups)[number];

// Ordine di visualizzazione dei gruppi nel menu.
export const placeholderCategoryGroups = [
  PlaceholderCategoryGroupEnum.Backoffice,
  PlaceholderCategoryGroupEnum.Frontoffice,
] as const;

export interface PlaceholderCategoryGroupMetadata {
  label: ReactNode;
}

// Registro dei gruppi (label + icona). Usato solo dal PlaceholderCategoryMenu
// per costruire gli item `type: 'group'`.
export const placeholderCategoryGroupRegistry: Record<
  PlaceholderCategoryGroup,
  PlaceholderCategoryGroupMetadata
> = {
  [PlaceholderCategoryGroupEnum.Backoffice]: {
    label: (
      <PlaceholderCategoryGroupMenuLabel
        title="Procedimento"
        subtitle="Backoffice"
      />
    ),
  },
  [PlaceholderCategoryGroupEnum.Frontoffice]: {
    label: (
      <PlaceholderCategoryGroupMenuLabel
        title="Richiesta"
        subtitle="Frontoffice"
      />
    ),
  },
};

export interface PlaceholderCategoryMetadata {
  label: string;
  icon: ReactNode;
  group: PlaceholderCategoryGroup;
  description?: string;
}

const DEFAULT_CATEGORY_ICON: ReactNode = <FolderOutlined />;

// Record ESAUSTIVO: ogni PlaceholderCategory nota DEVE avere una entry.
// L'aggiunta di una nuova categoria in types.ts causa un errore di
// compilazione finché non si aggiunge qui la relativa metadata.
export const placeholderCategoryRegistry: Record<
  PlaceholderCategory,
  PlaceholderCategoryMetadata
> = {
  [PlaceholderCategoryEnum.Procedimento]: {
    label: 'Procedimento',
    icon: <FileOutlined />,
    group: PlaceholderCategoryGroupEnum.Backoffice,
    description: 'Dati identificativi del procedimento',
  },
  [PlaceholderCategoryEnum.UserTask]: {
    label: 'Attività',
    icon: <EditOutlined />,
    group: PlaceholderCategoryGroupEnum.Backoffice,
    description: 'Informazioni sulle attività di lavorazione del procedimento',
  },
  [PlaceholderCategoryEnum.SezioneBackoffice]: {
    label: 'Sezioni di Backoffice',
    icon: <GroupOutlined />,
    group: PlaceholderCategoryGroupEnum.Backoffice,
    description: 'Valori dei campi delle sezioni dati compilate in backoffice',
  },
  [PlaceholderCategoryEnum.SezioneFrontoffice]: {
    label: 'Sezioni della Richiesta',
    icon: <GroupOutlined />,
    group: PlaceholderCategoryGroupEnum.Frontoffice,
    description: 'Valori dei campi compilati dal richiedente nella richiesta',
  },
  [PlaceholderCategoryEnum.TipoDocumento]: {
    label: 'Documenti',
    icon: <FileTextOutlined />,
    group: PlaceholderCategoryGroupEnum.Backoffice,
    description: 'Dati dei documenti generati nel corso del procedimento',
  },
  [PlaceholderCategoryEnum.TipoComunicazione]: {
    label: 'Comunicazioni',
    icon: <MailOutlined />,
    group: PlaceholderCategoryGroupEnum.Backoffice,
    description: 'Dati delle comunicazioni inviate nel corso del procedimento',
  },
  [PlaceholderCategoryEnum.Pratica]: {
    label: 'Pratica',
    icon: <ContainerOutlined />,
    group: PlaceholderCategoryGroupEnum.Frontoffice,
    description: 'Dati identificativi della pratica',
  },
  [PlaceholderCategoryEnum.RecordAlbo]: {
    label: 'Record albo',
    icon: DEFAULT_CATEGORY_ICON,
    group: PlaceholderCategoryGroupEnum.Backoffice,
    description: "Dati del record pubblicato all'albo",
  },
  [PlaceholderCategoryEnum.SalvaInTabella]: {
    label: 'Salva in tabella',
    icon: DEFAULT_CATEGORY_ICON,
    group: PlaceholderCategoryGroupEnum.Backoffice,
    description: 'Valori salvati in tabella durante la lavorazione',
  },
  [PlaceholderCategoryEnum.Funzione]: {
    label: 'Funzione',
    icon: <FunctionOutlined />,
    group: PlaceholderCategoryGroupEnum.Backoffice,
    description: "Funzioni per l'inserimento di valori dinamici o calcolati",
  },
  [PlaceholderCategoryEnum.Domanda]: {
    label: 'Domande',
    icon: <FormOutlined />,
    group: PlaceholderCategoryGroupEnum.Frontoffice,
    description: 'Dati della domanda presentata dal richiedente',
  },
  [PlaceholderCategoryEnum.Altro]: {
    label: 'Altro',
    icon: <EllipsisOutlined />,
    group: PlaceholderCategoryGroupEnum.Backoffice,
    description:
      'Placeholder di utilità generale, come la numerazione delle pagine',
  },
};

export function getPlaceholderCategoryMetadata(
  category: PlaceholderCategory,
): PlaceholderCategoryMetadata {
  return placeholderCategoryRegistry[category];
}

export function getPlaceholderCategoryGroupMetadata(
  group: PlaceholderCategoryGroup,
): PlaceholderCategoryGroupMetadata {
  return placeholderCategoryGroupRegistry[group];
}
